import { appData } from 'appData';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, useResolvedPath } from 'react-router-dom';
import { useUIDSeed } from 'react-uid';

interface Crumb {
  key: string;
  title: React.JSX.Element;
  url: string;
}

const BreadCrumbsContext = createContext<
  [Crumb[], React.Dispatch<React.SetStateAction<Crumb[]>>]
>([[], () => {}]);

export const BreadCrumbsProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);

  return (
    <BreadCrumbsContext.Provider value={[crumbs, setCrumbs]}>
      {children}
    </BreadCrumbsContext.Provider>
  );
};

export const BreadCrumbs: React.FC = () => {
  const [crumbs] = useContext(BreadCrumbsContext);

  const orderedCrumbs = crumbs.sort(
    (crumbA, crumbB) => crumbA.url.length - crumbB.url.length,
  );

  return (
    <ul className="flex flex-row py-2 space-x-3 px-0 text-gray-500 text-sm">
      {orderedCrumbs.map((crumb, index, list) => (
        <li key={crumb.key} className="flex flex-row items-center space-x-3">
          {index === list.length - 1 ? (
            <span>{crumb.title}</span>
          ) : (
            <>
              <Link to={crumb.url} className="underline hover:no-underline">
                {crumb.title}
              </Link>
              <span aria-hidden="true">
                <svg
                  role="presentation"
                  className={'fill-current block w-4 h-4 transform -rotate-90'}
                >
                  <use
                    xlinkHref={`${appData.assets.icons}#icon-chevron-thin-down`}
                  />
                </svg>
              </span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
};

interface CrumbProps {
  title: Crumb['title'];
}

export const Crumb: React.FC<CrumbProps> = ({ title }) => {
  const seed = useUIDSeed();
  // `useResolvedPath('')` returns the closest matched route's pathnameBase
  // (the URL up to but excluding any `*` wildcard portion), mirroring the v5
  // `useRouteMatch().url` semantic that this component used to rely on.
  const { pathname: url } = useResolvedPath('');
  const [_, setCrumbs] = useContext(BreadCrumbsContext);

  useEffect(() => {
    const key = seed(url);
    setCrumbs((crumbs) => [...crumbs, { key, title, url }]);

    return () => {
      setCrumbs((crumbs) =>
        crumbs.filter((currentCrumb) => currentCrumb.key !== key),
      );
    };
  }, []);

  return null;
};
